import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to manage the active cursor's position and width.
 *
 * @param {number} activeIndex - The index of the active item.
 * @param {object} containerRef - The ref of the container holding the items.
 * @returns {object} - Styles for the active cursor.
 */
const useActiveCursor = ({ activeIndex, containerRef, offset = 0 }, deps = []) => {
  const [cursorStyle, setCursorStyle] = useState({});
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);

  const getActiveItem = useCallback(() => {
    return containerRef?.current?.children?.[activeIndex];
  }, [containerRef, activeIndex]);

  const updateCursor = useCallback(() => {
    const activeItem = getActiveItem();
    if (!activeItem) {
      return;
    }

    const offsetLeft = activeItem.offsetLeft + offset;
    const offsetWidth = activeItem.offsetWidth + offset * 2;

    setCursorStyle({
      transform: `translateX(${offsetLeft}px)`,
      width: `${offsetWidth}px`
    });
  }, [getActiveItem, offset, activeRouteId, ...deps]);

  useEffect(() => {
    const activeItem = getActiveItem();
    if (!activeItem) return;

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        updateCursor();
      });
    });
    resizeObserver.observe(activeItem);

    window.addEventListener('resize', updateCursor);
    requestAnimationFrame(updateCursor);

    return () => {
      resizeObserver.unobserve(activeItem);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCursor);
    };
  }, [getActiveItem, updateCursor]);

  return cursorStyle;
};

export default useActiveCursor;
